import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Descriptions,
  Drawer,
  Form,
  Select,
  Space,
  Steps,
  Typography,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  iamApi as iamHttpApi,
  type GetPolicyResponse,
  type GrantPolicyRequest,
  type IamPolicySearchRecord,
  type ListIAMUserGroupsRequest,
  type SearchIdentityPoliciesParams,
} from '@/api/iam.api';
import { useIamUserGroupList } from '@/pages/iam/composables/useIamGroups';
import { useSavePolicyGrant } from '@/pages/iam/composables/useGrantPolicyData';
import { useIamUserList } from '@/pages/iam/composables/useIamUsers';
import {
  PolicyCardSelector,
  PolicyPreviewDrawer,
  PolicySummaryCard,
  type PolicyPreviewData,
  type PolicySelectionItem,
} from '@/pages/iam/components/PolicySelector';
import type { ListIAMUsersRequest } from '@/types/iam.types';
import './GrantPermissionDrawer.css';

type IamPrincipalType = 'USER' | 'GROUP';

const ALL_USERS_REQUEST: ListIAMUsersRequest = {
  page: 1,
  limit: -1,
  order_by: ['-created_at'],
  name: '',
  display_name: '',
};

const ALL_USER_GROUPS_REQUEST: ListIAMUserGroupsRequest = {
  page: 1,
  limit: -1,
  name: '',
  display_name: '',
  keyword: '',
};

const GRANT_POLICY_SEARCH_PARAMS = {
  limit: '100',
  offset: '0',
  order_by: 'created_at',
} satisfies Omit<SearchIdentityPoliciesParams, 'identity_type' | 'identity'>;

function toPolicySelectionItem(policy: IamPolicySearchRecord): PolicySelectionItem {
  return {
    id: policy.policy_id,
    name: policy.name,
    description: policy.description,
    type: policy.type === 'system' ? 'SYSTEM' : 'CUSTOM',
    statementCount: policy.statement_count,
    permissionCount: policy.action_count,
  };
}

function toPolicyPreview(policy: GetPolicyResponse['data']): PolicyPreviewData {
  return {
    id: policy.policy_id,
    name: policy.name,
    description: policy.description,
    type: policy.type === 'system' ? 'SYSTEM' : 'CUSTOM',
    authorizationCount: policy.authorization_count,
    createdAt: new Date(policy.created_at * 1000).toISOString(),
    statements: policy.policy_config.statement.map((statement) => ({
      effect: statement.effect.toLowerCase() === 'deny' ? 'Deny' : 'Allow',
      actions: statement.action,
      resourceScope:
        statement.condition?.string_equals?.['inf:resourceOwner'] === '{self}' ? 'OWN' : 'ALL',
    })),
    permissionActions: policy.permission_summary.actions,
    source: policy.policy_config,
  };
}

export interface GrantPermissionPreset {
  principalType?: IamPrincipalType;
  principalId?: string;
  policyId?: string;
}

interface Props {
  open: boolean;
  preset?: GrantPermissionPreset;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
}

interface GrantFormValues {
  principalType: IamPrincipalType;
  principalId: string;
  policyIds: string[];
}

export default function GrantPermissionDrawer({ open, preset, onClose, onSuccess }: Props) {
  const { message, modal } = App.useApp();
  const [form] = Form.useForm<GrantFormValues>();
  const [step, setStep] = useState(0);
  const [previewPolicyId, setPreviewPolicyId] = useState<string>();
  const [previewPolicyOpen, setPreviewPolicyOpen] = useState(false);
  const savePolicyGrant = useSavePolicyGrant();
  const watchedPolicyIds = Form.useWatch('policyIds', form);
  const selectedPolicyIds = useMemo(() => watchedPolicyIds || [], [watchedPolicyIds]);
  const principalType = Form.useWatch('principalType', form);
  const principalId = Form.useWatch('principalId', form);

  const policyQueryParams = useMemo<SearchIdentityPoliciesParams | undefined>(() => {
    if (!principalType || !principalId) return undefined;
    return {
      ...GRANT_POLICY_SEARCH_PARAMS,
      identity_type: principalType === 'USER' ? 'user' : 'user_group',
      identity: principalId,
    };
  }, [principalId, principalType]);

  const usersQuery = useIamUserList(ALL_USERS_REQUEST, open && principalType === 'USER');
  const userGroupsQuery = useIamUserGroupList(
    ALL_USER_GROUPS_REQUEST,
    open && principalType === 'GROUP'
  );
  const policiesQuery = useQuery({
    queryKey: ['iam', 'policies', 'grant-selector', policyQueryParams] as const,
    queryFn: async () => {
      if (!policyQueryParams) {
        throw new Error('请先选择授权主体');
      }
      const response = await iamHttpApi.searchIdentityPolicies(policyQueryParams);
      if (response.code !== 0) {
        throw new Error(response.message || '搜索权限策略失败');
      }
      return response;
    },
    enabled: open && !!policyQueryParams,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const policyDetailQuery = useQuery({
    queryKey: ['iam', 'policies', 'detail', previewPolicyId] as const,
    queryFn: async () => {
      const response = await iamHttpApi.getPolicy(previewPolicyId as string);
      if (response.code !== 0) {
        throw new Error(response.message || '获取权限策略详情失败');
      }
      return response;
    },
    enabled: open && previewPolicyOpen && !!previewPolicyId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const policies = useMemo(
    () => (policiesQuery.data?.data.policies ?? []).map(toPolicySelectionItem),
    [policiesQuery.data]
  );
  const grantedPolicyIds = useMemo(
    () =>
      (policiesQuery.data?.data.policies ?? [])
        .filter((policy) => policy.is_grant)
        .map((policy) => policy.policy_id),
    [policiesQuery.data]
  );
  const previewPolicy = useMemo(
    () => (policyDetailQuery.data ? toPolicyPreview(policyDetailQuery.data.data) : undefined),
    [policyDetailQuery.data]
  );
  const userOptions = useMemo(
    () =>
      (usersQuery.data?.items ?? []).map((user) => ({
        value: user.id,
        label: `${user.name}（${user.id}）`,
      })),
    [usersQuery.data]
  );
  const groupOptions = useMemo(
    () =>
      (userGroupsQuery.data?.items ?? []).map((group) => ({
        value: group.id,
        label: `${group.display_name || group.name}（${group.id}）`,
      })),
    [userGroupsQuery.data]
  );
  const selectedPolicies = useMemo(
    () =>
      selectedPolicyIds
        .map((id) => policies.find((policy) => policy.id === id))
        .filter((policy): policy is PolicySelectionItem => Boolean(policy)),
    [policies, selectedPolicyIds]
  );

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setPreviewPolicyOpen(false);
    setPreviewPolicyId(undefined);
    form.resetFields();
    form.setFieldsValue({
      principalType: preset?.principalType || 'GROUP',
      principalId: preset?.principalId || '',
      policyIds: preset?.policyId ? [preset.policyId] : [],
    });
  }, [form, open, preset]);

  useEffect(() => {
    if (!open || !policiesQuery.data) return;
    const nextPolicyIds = selectedPolicyIds.filter((id) => !grantedPolicyIds.includes(id));
    if (nextPolicyIds.length !== selectedPolicyIds.length) {
      form.setFieldValue('policyIds', nextPolicyIds);
    }
  }, [form, grantedPolicyIds, open, policiesQuery.data, selectedPolicyIds]);

  const selectedUser = usersQuery.data?.items.find((user) => user.id === principalId);
  const selectedUserGroup = userGroupsQuery.data?.items.find((group) => group.id === principalId);
  const principalName =
    principalType === 'USER'
      ? selectedUser?.name
      : selectedUserGroup?.display_name || selectedUserGroup?.name;
  const principalExists = principalType === 'USER' ? !!selectedUser : !!selectedUserGroup;
  const principalQueryError = principalType === 'USER' ? usersQuery.error : userGroupsQuery.error;
  const principalLoading =
    principalType === 'USER' ? usersQuery.isFetching : userGroupsQuery.isFetching;
  const visiblePolicies = preset?.policyId
    ? policies.filter((policy) => policy.id === preset.policyId)
    : policies;

  const openPolicyPreview = (policy: PolicySelectionItem) => {
    setPreviewPolicyId(policy.id);
    setPreviewPolicyOpen(true);
  };

  const next = async () => {
    await form.validateFields(['principalType', 'principalId', 'policyIds']);
    setStep(1);
  };

  const buildRequest = (values: GrantFormValues): GrantPolicyRequest => ({
    policy_ids: values.policyIds,
    identities: [
      {
        identity_type: values.principalType === 'USER' ? 'user' : 'user_group',
        identity: values.principalId,
      },
    ],
  });

  const execute = async (request: GrantPolicyRequest) => {
    try {
      await savePolicyGrant.mutateAsync(request);
      message.success(`已创建 ${request.policy_ids.length} 条授权记录`);
      await onSuccess();
      onClose();
    } catch {
      // 请求错误由全局请求层统一提示。
    }
  };

  const submit = async () => {
    const values = await form.validateFields();
    const request = buildRequest(values);
    const shouldConfirm = request.policy_ids.length > 1 || values.principalType === 'GROUP';
    if (shouldConfirm) {
      modal.confirm({
        title: '确认批量授权影响',
        content:
          values.principalType === 'GROUP'
            ? `将向 ${principalName || '所选用户组'} 授予 ${request.policy_ids.length} 条策略，并影响该用户组中的全部成员。任一项校验失败时整批不会生效。`
            : `将向 ${principalName || '所选用户'} 授予 ${request.policy_ids.length} 条策略。任一项校验失败时整批不会生效。`,
        okText: '确认授权',
        onOk: () => execute(request),
      });
      return;
    }
    await execute(request);
  };

  return (
    <Drawer
      title="添加权限"
      width={720}
      open={open}
      onClose={onClose}
      destroyOnHidden
      footer={
        <div className="iam-grant-drawer-footer">
          <Button onClick={onClose}>取消</Button>
          {step === 1 && <Button onClick={() => setStep(0)}>上一步</Button>}
          {step === 0 ? (
            <Button
              type="primary"
              disabled={principalLoading || !!principalQueryError}
              onClick={next}
            >
              下一步
            </Button>
          ) : (
            <Button type="primary" loading={savePolicyGrant.isPending} onClick={submit}>
              确认授权
            </Button>
          )}
        </div>
      }
    >
      <Steps size="small" current={step} items={[{ title: '选择权限' }, { title: '确认授权' }]} />
      <Form form={form} layout="vertical" className="iam-grant-drawer-form">
        <div hidden={step !== 0}>
          {policiesQuery.error && (
            <Alert
              type="error"
              showIcon
              message="权限策略加载失败"
              description={
                policiesQuery.error instanceof Error
                  ? policiesQuery.error.message
                  : String(policiesQuery.error)
              }
            />
          )}
          <div className="iam-grant-drawer-section">
            <Typography.Title level={5}>授权主体</Typography.Title>
            {principalQueryError && (
              <Alert
                type="error"
                showIcon
                message="授权主体加载失败"
                description={
                  principalQueryError instanceof Error
                    ? principalQueryError.message
                    : String(principalQueryError)
                }
              />
            )}
            <Space.Compact block>
              <Form.Item name="principalType" noStyle rules={[{ required: true }]}>
                <Select
                  disabled={!!preset?.principalType}
                  onChange={() => {
                    form.setFieldValue('principalId', undefined);
                    form.setFieldValue('policyIds', preset?.policyId ? [preset.policyId] : []);
                  }}
                  options={[
                    { value: 'USER', label: '用户' },
                    { value: 'GROUP', label: '用户组' },
                  ]}
                  style={{ width: 120 }}
                />
              </Form.Item>
              <Form.Item
                name="principalId"
                noStyle
                rules={[
                  { required: true, message: '请选择授权主体' },
                  {
                    validator: (_, value) =>
                      !value || principalLoading || principalExists
                        ? Promise.resolve()
                        : Promise.reject(new Error('授权主体不存在或已失效')),
                  },
                ]}
              >
                <Select
                  disabled={!!preset?.principalId}
                  onChange={() =>
                    form.setFieldValue('policyIds', preset?.policyId ? [preset.policyId] : [])
                  }
                  showSearch
                  optionFilterProp="label"
                  placeholder={principalType === 'USER' ? '选择用户' : '选择用户组'}
                  options={principalType === 'USER' ? userOptions : groupOptions}
                  loading={principalLoading}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Space.Compact>
          </div>

          <div className="iam-grant-drawer-section">
            <Typography.Title level={5}>权限策略</Typography.Title>
            <Form.Item
              name="policyIds"
              rules={[{ required: true, message: '至少选择一个权限策略' }]}
            >
              <PolicyCardSelector
                policies={visiblePolicies}
                loading={policiesQuery.isFetching}
                disabled={!!preset?.policyId}
                disabledPolicyIds={grantedPolicyIds}
                onPreview={openPolicyPreview}
              />
            </Form.Item>
            <Typography.Text type="secondary">
              资源范围由策略正文决定。授权时只建立“主体—策略”关系，不再单独修改范围。
            </Typography.Text>
          </div>
        </div>

        <div hidden={step !== 1} className="iam-grant-drawer-preview">
          <Typography.Text type="secondary">
            {principalType === 'GROUP' ? '预计影响该用户组中的全部成员' : '预计影响 1 名用户'}
          </Typography.Text>
          <Descriptions
            column={1}
            bordered
            size="small"
            items={[
              {
                key: 'principal',
                label: '授权主体',
                children: principalName || principalId || '—',
              },
              {
                key: 'policies',
                label: '权限策略',
                children: selectedPolicies.map((policy) => policy.name).join('、'),
              },
            ]}
          />
          <div className="iam-policy-selector-card-grid">
            {selectedPolicies.map((policy) => (
              <PolicySummaryCard key={policy.id} policy={policy} onPreview={openPolicyPreview} />
            ))}
          </div>
        </div>
      </Form>
      <PolicyPreviewDrawer
        open={previewPolicyOpen}
        policy={previewPolicy}
        loading={policyDetailQuery.isFetching}
        error={
          policyDetailQuery.error
            ? policyDetailQuery.error instanceof Error
              ? policyDetailQuery.error.message
              : String(policyDetailQuery.error)
            : undefined
        }
        onClose={() => setPreviewPolicyOpen(false)}
        afterClose={() => setPreviewPolicyId(undefined)}
      />
    </Drawer>
  );
}
